import { IForm } from 'core-components/classes/models/IForm'

// SMB vs EU split.
// Sp returns every logical row tripled: Total + SMB + EU with the same flags,
// so any data selection must be narrowed by this field to get a single row.
// Absent in responses of sp versions without SMB vs EU split, treat as Total.
export enum SMBFractionTypes {
  Total = 'Total',
  SMB = 'SMB',
  EU = 'EU',
}

export interface IJBPPlanning {
  PartnerId: string
  FYear: number
  ProgramTypes: string
  Category: string
  FQuarter: number
  SubCategory: string
  L3Category: string

  SMBFraction?: SMBFractionTypes | null

  // Global Partner categories

  GlobalCategory?: string | null

  // Data classification, only one of these flags can be set in the data

  IsActuals: number
  IsDirect: number
  IsTarget: number
  IsQuota: number
  IsNoSplit: number // For Composed Report Planing
  IsRollUp: number // For Global Partner Roll Up
  IsTotal: number // For Total widget on Planning
  IsPartnerCount: number

  // Flags

  IsCollapsed: number
  Comment?: string | null
  Justification?: string | null
  JustificationSob?: string | null
  JustificationContractual?: string | null
  JustificationTransactional?: string | null
  JustificationUnitsContractual?: string | null
  JustificationUnitsTransactional?: string | null

  SOBState?: number

  IsNoPlan: number
  IsNoPlanContractual: number

  Currency: string
  NetNdp: string

  SellThruSumUSDTNet: number | null
  SellThruSumUSDUTNet?: number | null
  SellThruSumUSDNet: number | null
  SellThruSumUSDNdp: number | null

  SellThruSumLCTNet: number | null // No in spJBPComposedReport
  SellThruSumLCUTNet?: number | null // No in spJBPComposedReport
  SellThruSumLCNet: number | null // No in spJBPComposedReport
  SellThruSumLCNdp: number | null // No in spJBPComposedReport

  SellThruUnitsTNet: number | null
  SellThruUnitsUTNet?: number | null
  SellThruUnits: number | null
  SellThruUnitsNdp: number | null

  // Direct (Actuals only)

  SellThruSumUSDTNetDirect: number | null
  SellThruSumUSDUTNetDirect?: number | null
  SellThruSumUSDNetDirect: number | null
  SellThruSumUSDNdpDirect: number | null

  SellThruSumLCTNetDirect: number | null // No in spJBPComposedReport
  SellThruSumLCUTNetDirect?: number | null // No in spJBPComposedReport
  SellThruSumLCNetDirect: number | null // No in spJBPComposedReport
  SellThruSumLCNdpDirect: number | null // No in spJBPComposedReport

  SellThruUnitsTNetDirect: number | null
  SellThruUnitsUTNetDirect?: number | null
  SellThruUnitsDirect: number | null
  SellThruUnitsNdpDirect: number | null

  // For Contractual / Transactional planning

  ContractualSumUSDTNet: number | null
  ContractualSumUSDUTNet?: number | null
  ContractualSumUSDNet: number | null
  ContractualSumUSDNdp: number | null

  ContractualSumLCTNet: number | null // No in spJBPComposedReport
  ContractualSumLCUTNet?: number | null // No in spJBPComposedReport
  ContractualSumLCNet: number | null // No in spJBPComposedReport
  ContractualSumLCNdp: number | null // No in spJBPComposedReport

  ContractualUnitsTNet: number | null
  ContractualUnitsUTNet?: number | null
  ContractualUnits: number | null
  ContractualUnitsNdp: number | null

  // Direct (Actuals only)

  ContractualSumUSDTNetDirect: number | null
  ContractualSumUSDUTNetDirect?: number | null
  ContractualSumUSDNetDirect: number | null
  ContractualSumUSDNdpDirect: number | null

  ContractualSumLCTNetDirect: number | null // No in spJBPComposedReport
  ContractualSumLCUTNetDirect?: number | null // No in spJBPComposedReport
  ContractualSumLCNetDirect: number | null // No in spJBPComposedReport
  ContractualSumLCNdpDirect: number | null // No in spJBPComposedReport

  ContractualUnitsTNetDirect: number | null
  ContractualUnitsUTNetDirect?: number | null
  ContractualUnitsDirect: number | null
  ContractualUnitsNdpDirect: number | null

  // SoB

  SoB: number | null
  SoBCompleted: number | null
  FinYoYGrowth?: number | null
  SobYoYGrowth?: number | null

  // Partners Completeness

  ReportGUID: string

  // In Composed Report Planing mode, show information:
  // Show all: (i) 2/1. Show only fully completed plans: (i) 2/3

  PartnersCount: number // 1 - present, used
  PartnersCountAcknowledged: number // 2 - present, used
  PartnersCountNotAcknowledged: number // present, not used
  PartnersNotFullyCompleted: number // reserved (missing in sp now)

  PartnersCompleted: number // 3 - present, used
  PartnersNotCompleted: number // 4 - present, used
  PartnersCompletedAcknowledged: number // reserved (missing in sp now)
  PartnersNotCompletedAcknowledged: number // reserved (missing in sp now)

  SoBPartnersCompleted: number
  SoBPartnersNotFullyCompleted: number

  SellThruSumUSDNdpCompleted: number | null
  SellThruSumUSDNdpCompletedSob: number | null
  SellThruSumUSDNetCompleted: number | null
  SellThruSumUSDNetCompletedSob: number | null
  SellThruUnitsNdpCompleted: number | null
  SellThruUnitsCompleted: number | null

  ContractualSumUSDNdpCompleted: number | null
  ContractualSumUSDNdpCompletedSob: number | null
  ContractualSumUSDNetCompleted: number | null
  ContractualSumUSDNetCompletedSob: number | null
  ContractualUnitsNdpCompleted: number | null
  ContractualUnitsCompleted: number | null
}

// Transient fields
export interface IJBPPlanningItem extends IJBPPlanning, IForm {
  IsDataPresent: boolean
  IsAcknowledgedDataPresent: boolean

  IsChanged: number
  IsChangedSoB: number

  IsChangedSoBManual: number

  IsChangedManual: number
  IsChangedContractualManual: number
  IsChangedTransactionalManual: number

  IsChangedUnitsManual: number
  IsChangedUnitsContractualManual: number
  IsChangedUnitsTransactionalManual: number
}
